'use client';

import '@/app/globals.css';
import React, { PropsWithChildren, useEffect, useState, useRef } from "react";
import { usePathname, useRouter } from 'next/navigation'; // Import useRouter
import { useSession, useUser } from '@descope/nextjs-sdk/client';
import * as _descope_core_js_sdk from '@descope/core-js-sdk';
import { analytics } from '@/app/analytics';
import { ScrollProvider } from './ui/scroll-context';
import AppLoading from './ui/app-loading';
import { useTrialHeaderBanner } from '../hooks/useTrialHeaderBanner';
import dynamic from 'next/dynamic';
import { UserDataProvider } from '@/components/navigation/contexts/user-data-context';
import { useUserData } from '@/components/navigation/hooks/useUserData';
import { needsOnboarding } from '@/utils/onboarding';
import ErrorBoundary from './error-boundary';
import { WorkspaceHeader } from '@/components/navigation/workspace-header';
import { GlobalNotifications } from '@/components/global-notifications';
import type { RumUserContext } from '@/app/rum';

// Dynamic import for debug component (only for production error testing)
const ErrorTrigger = dynamic(() => import('./debug/ErrorTrigger'), { ssr: false });

// Dynamic imports for non-critical components
const NavBar = dynamic(() => import('./navigation/nav-bar').then(mod => mod.NavBar), {
    loading: () => <div className="animate-pulse h-full w-[280px] bg-gray-100" />
});

const UsedAllCreditsModal = dynamic(() => import('./ui/used-all-credits-modal').then(mod => mod.default));
const TrialHeaderBanner = dynamic(() => import('./trial/trial-header-banner').then(mod => mod.default));
const AboutTrialDialog = dynamic(() => import('./trial/about-trial-dialog').then(mod => mod.default));
const PostOnboardingDialog = dynamic(() => import('./ui/post-onboarding-dialog').then(mod => mod.default));
const TrialInfoDialog = dynamic(() => import('./trial/trial-info-dialog').then(mod => mod.default));
const TrialEndedDialog = dynamic(() => import('./trial/trial-ended-dialog').then(mod => mod.default));

import { TrialProvider } from '@/components/trial/trial-context';

async function initRum() {
    if (window && typeof window !== 'undefined') {
        try {
            const rumAnalytics = (await import('@/app/rum')).rumAnalytics;
            rumAnalytics.initialize();
        } catch (error) {
            console.warn('[PageLayout] Failed to initialize RUM', error);
        }
    }
}

async function setRumUser(userContext: RumUserContext) {
    if (window && typeof window !== 'undefined') {
        try {
            const rumAnalytics = (await import('@/app/rum')).rumAnalytics;
            await rumAnalytics.setUserContext(userContext);
        } catch (error) {
            console.warn('[PageLayout] Failed to set RUM user context', error);
        }
    }
}

export const PageLayout: React.FC<PropsWithChildren> = ({ children }) => {
    const { isUserLoading, user } = useUser();
    const { isAuthenticated, isSessionLoading } = useSession();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const location = usePathname();
    const router = useRouter();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const userIdRef = useRef<string | null>(null);
    const isListViewPage = location === '/lists' || location === '/contacts/lists';
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const is10kDemo = searchParams.get('10kdemo') === 'true' || searchParams.get('decision-maker') === 'true';
    const showUsedAllCreditsModal = searchParams.get('used-all-credits') === 'true';
    const modalOpenCountRef = useRef(0);
    const [onboardingChecked, setOnboardingChecked] = useState(false);
    const [shouldRedirectOnboarding, setShouldRedirectOnboarding] = useState<boolean | null>(null);

    // Use the useUserData hook to get team and preference data
    const userData = useUserData();
    const {
        userDetails,
        isUserDetailsLoading,
        isUserBootstrapFetching,
        team,
        teamPreferences,
        isLoadingTeamPreferences,
        isLoadingUserTeams,
        effectiveProducts,
        effectiveBuyingPersona
    } = userData;
    const { subscription, showTrialHeader, isTrialEnded } = useTrialHeaderBanner(userDetails);

    useEffect(() => {
        analytics.initialize();
        initRum();

        // Enhanced error handling for React errors
        const handleUnhandledError = (event: ErrorEvent) => {
            console.error('Unhandled error:', event.error);

            // Send additional context to RUM
            if (typeof window !== 'undefined' && (window as any).CoralogixRum) {
                (window as any).CoralogixRum.error(event.error?.message || 'Unhandled Error', {
                    source: 'unhandled-error',
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                    timestamp: new Date().toISOString(),
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    errorStack: event.error?.stack
                });
            }
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            console.error('Unhandled promise rejection:', event.reason);

            // Send promise rejection to RUM
            if (typeof window !== 'undefined' && (window as any).CoralogixRum) {
                (window as any).CoralogixRum.error('Unhandled Promise Rejection', {
                    source: 'unhandled-promise-rejection',
                    reason: event.reason,
                    timestamp: new Date().toISOString(),
                    url: window.location.href,
                    errorMessage: typeof event.reason === 'string' ? event.reason : JSON.stringify(event.reason)
                });
            }
        };

        // Listen for custom error events from error boundary
        const handleCustomError = (event: CustomEvent) => {
            console.error('Custom error event:', event.detail);

            if (typeof window !== 'undefined' && (window as any).CoralogixRum) {
                (window as any).CoralogixRum.error(event.detail.message || 'Custom Error Event', {
                    source: 'custom-error-event',
                    ...event.detail
                });
            }
        };

        window.addEventListener('error', handleUnhandledError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        window.addEventListener('clearitty-error', handleCustomError as EventListener);

        return () => {
            window.removeEventListener('error', handleUnhandledError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            window.removeEventListener('clearitty-error', handleCustomError as EventListener);
        };
    }, []);

    // Set CSS variable for trial banner height so child pages (e.g. company table) can adjust layout
    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty(
                '--trial-banner-height',
                showTrialHeader && subscription ? '48px' : '0px'
            );
        }
    }, [showTrialHeader, subscription]);

    useEffect(() => {
        if (user) {
            const userId = user.customAttributes && user.customAttributes['clearittyId'] ? user.customAttributes['clearittyId'] : undefined;

            // Only run these once per user ID
            if (userId && userIdRef.current !== userId) {
                userIdRef.current = userId;
                setRumUser({
                    id: userId,
                    email: user.email,
                    name: user.name,
                });
                // initIntercom(userId, user); // Disabled
                analytics.identify(userId, { email: user.email, name: user.name, anonEmail: user?.customAttributes?.anonEmail ? user.customAttributes?.anonEmail : '' });
            }
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            analytics.getTrackerInstance().appPageViewed({
                pageTitle: location,
                pagePath: location,
            });
        }
    }, [location]);

    useEffect(() => {
        if (user && user.email && user.email.includes('anon.clearitty.com') && !location.startsWith('/survey') && !location.startsWith('/welcome')) {
            router.push('/survey/new');
        }
    }, [user, location, router]);

    const pathArray = location.split('/');
    const page = pathArray[pathArray.length - 1];
    const pageParent = pathArray.length > 1 ? pathArray[pathArray.length - 2] : '';
    const isSpecialPage = [
        'login', 'onboarding', 'survey', 'welcome', 'logout', 'start', 'checkout', 'pricing-table', 'pricing', 'test-embed'
    ].includes(page) || [
        'login', 'onboarding', 'survey', 'welcome', 'start', 'checkout', 'embed'
    ].includes(pageParent);

    useEffect(() => {
        let ignore = false;
        function checkOnboarding() {
            if (isSessionLoading) return;

            if (!isAuthenticated || isSpecialPage || (location === '/' && is10kDemo)) {
                if (!ignore) {
                    setShouldRedirectOnboarding(false);
                    setOnboardingChecked(true);
                }
                return;
            }

            if (isUserDetailsLoading || isUserBootstrapFetching || isLoadingUserTeams || isLoadingTeamPreferences) {
                return;
            }

            if (!userDetails) {
                if (!ignore) {
                    setShouldRedirectOnboarding(false);
                    setOnboardingChecked(true);
                }
                return;
            }

            if (!isSpecialPage) {

                const onboarding = userDetails.onBoardingState;
                console.log('[PageLayout] User onboarding state:', onboarding);

                const onboardingData = {
                    userDetails,
                    team,
                    teamPreferences: teamPreferences ?? null,
                    effectiveProducts,
                    effectiveBuyingPersona
                };

                // Don't redirect to onboarding from billing page when arriving via partner UTM link
                // — the billing page server component applies partner config (skip onboarding)
                const isBillingWithPartnerUtm = location.includes('/earn-credits/billing') &&
                    searchParams.get('utm_medium');

                if (needsOnboarding(onboardingData) && !isBillingWithPartnerUtm) {
                    if (!ignore && !location.includes('/onboarding')) {
                        console.log('[PageLayout] User needs onboarding (fallback check), redirecting to /onboarding');
                        console.log('[PageLayout] Current location:', location);
                        setShouldRedirectOnboarding(true);

                        // Use replace instead of push to avoid back button issues
                        router.replace('/onboarding');
                    }
                } else {
                    // Onboarding is complete, ensure loader is not shown
                    setShouldRedirectOnboarding(false);
                }
            }
            if (!ignore) setOnboardingChecked(true);
        }
        checkOnboarding();
        return () => { ignore = true; };
    }, [isSessionLoading, isAuthenticated, location, router, is10kDemo, isSpecialPage, isUserDetailsLoading, isUserBootstrapFetching, isLoadingUserTeams, isLoadingTeamPreferences, userDetails, team, teamPreferences, effectiveProducts, effectiveBuyingPersona]);

    // If on a special page, skip onboarding check and loader, just render children
    if (isSpecialPage) {
        return (
            <ScrollProvider scrollContainerRef={scrollContainerRef}>
                <UserDataProvider value={userData}>
                    <div className={`flex flex-col w-full h-full min-h-screen ${page === 'pricing-table' ? 'bg-gray-100' : ''}`}>
                        {children}
                    </div>
                </UserDataProvider>
            </ScrollProvider>
        );
    }

    if (isSessionLoading || !onboardingChecked) {
        console.log('[PageLayout] Loader shown:', { isSessionLoading, onboardingChecked, shouldRedirectOnboarding });
        return <AppLoading />;
    }

    if (shouldRedirectOnboarding === true) {
        console.log('[PageLayout] Redirecting to onboarding from:', location);
        return <AppLoading />;
    }

    if (!isSessionLoading && !isAuthenticated) {
        return (
            <>
                {children}
            </>
        )
    }

    return (
        <ErrorBoundary>
            <ScrollProvider scrollContainerRef={scrollContainerRef}>
                <UserDataProvider value={userData}>
                    <TrialProvider>
                        <GlobalNotifications />
                        <div className={`flex flex-col w-full h-full min-h-screen ${(page === 'pricing-table' || page === 'pricing' || page === 'test-embed' || pageParent === 'embed') ? 'bg-gray-100' : ''}`}>
                            {showUsedAllCreditsModal && <UsedAllCreditsModal credits={0} />}
                            {/* Global trial header at the TOP of the page - DISABLED */}
                            {showTrialHeader && subscription && (
                                <TrialHeaderBanner subscription={subscription} isTrialEnded={isTrialEnded} />
                            )}
                            <AboutTrialDialog />
                            <PostOnboardingDialog mode="recurring" shouldShow={shouldRedirectOnboarding === false} />
                            <TrialInfoDialog />
                            <TrialEndedDialog />
                            {(page !== 'login' && page !== 'onboarding' && page !== 'survey' && pageParent !== 'survey' && page !== 'welcome' && page !== 'logout' && page !== 'start' && page !== 'checkout' && page !== 'pricing-table' && page !== 'pricing' && page !== 'test-embed' && pageParent !== 'embed') &&
                                <>
                                    <div className={`flex flex-col md:flex-row w-full flex-1 min-h-0 min-h-[calc(100vh-var(--trial-banner-height,0px)-4px)] transition-all duration-300 ease-in-out`}>
                                        <div className={`sticky top-[var(--trial-banner-height,0px)] hidden md:flex z-navbar bg-white h-[calc(100vh-var(--trial-banner-height,0px)-4px)]`}>
                                            <NavBar
                                                isCollapsed={isCollapsed}
                                                shouldShow={shouldRedirectOnboarding === false}
                                            />
                                        </div>
                                        <div
                                            className={`flex flex-col w-full flex-1 min-w-0 min-h-0 overflow-auto bg-natural-50 md:rounded-tl-2xl md:mt-[24px] pb-16 md:pb-0 transition-all duration-300 ease-in-out`}
                                            ref={scrollContainerRef}
                                        >
                                            <WorkspaceHeader />
                                            <ErrorBoundary>
                                                {children}
                                                {/* Debug component for production error testing */}
                                                <ErrorTrigger />
                                            </ErrorBoundary>
                                        </div>
                                    </div>
                                    {/* Mobile Navigation - using NavBar component in fixed position */}
                                    <div
                                        className={`!fixed !bottom-0 !left-0 !right-0 w-full md:hidden !z-50 ${isCollapsed ? 'md:hidden' : ''}`}
                                    >
                                        <div className="bg-white border-t border-gray-200">
                                            <div className="[&>div]:!static [&>div]:!w-full [&>div]:!h-auto">
                                                <NavBar
                                                    mobileMode
                                                    isCollapsed={false}
                                                    shouldShow={shouldRedirectOnboarding === false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>}
                            {(page === 'login' || page === 'onboarding' || page === 'survey' || pageParent === 'survey' || page === 'welcome' || page === 'logout' || page === 'start' || pageParent === 'welcome' || page === 'checkout' || page === 'pricing-table' || page === 'pricing' || page === 'test-embed' || pageParent === 'embed') && <>{children}</>}
                        </div>
                    </TrialProvider>
                </UserDataProvider>
            </ScrollProvider>
        </ErrorBoundary>
    );
};

export default PageLayout;